import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  MinusCircleIcon,
} from "@heroicons/react/24/outline";
import ProductSlideover from "../../products/product-slideover";
import { ReportPreviousWorkDialog } from "../../service/reports/report-previous-work";
import CustomerSystemDialog from "./system-dialog";
import { useDispatch } from "react-redux";
import { updateReportAsync } from "../../../../slices/service/reports";
import { dispatchWithToast } from "../../../Utils";
import { ServiceReportOperationObjectStatus } from "../../service/reports/utils";

function facilityAddressLabel(system) {
  const addr = system?.delivery_address;
  if (!addr) return null;
  if (addr.displayTitle) return addr.displayTitle;
  return [addr.address, addr.postal, addr.place].filter(Boolean).join(", ") || null;
}

function facilityComment(system) {
  return (
    system?.other_comment ||
    system?.description ||
    system?.notes ||
    ""
  ).trim();
}

const STATUS_PILL = {
  amber: "bg-amber-50 text-amber-800",
  green: "bg-emerald-50 text-emerald-700",
  gray: "bg-slate-100 text-slate-600",
};

export const CustomerSystemSection = ({
  system,
  module,
  moduleParam,
  customer,
  onRefresh,
  operations,
}) => {
  const { t } = useTranslation();
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [productSlideoverOpen, setProductSlideoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const handleOnAttachSystem = (data) => {
    if (module === "report" && moduleParam) {
      dispatchWithToast(
        dispatch(
          updateReportAsync({ id: moduleParam, system_id: data?.value })
        ),
        (refreshed) => {
          onRefresh?.(refreshed);
        }
      );
    }
  };

  const getSystemStatus = () => {
    if (!operations) return "UNKNOWN";
    return ServiceReportOperationObjectStatus({
      operations: operations,
      objectName: "system",
    });
  };

  const systemStatus = getSystemStatus();
  const hasSystem = !!system?.id;
  const addressLabel = facilityAddressLabel(system);
  const comment = facilityComment(system);

  const statusPill = (() => {
    if (!hasSystem) {
      return {
        tone: "amber",
        icon: ExclamationTriangleIcon,
        label: t("customer.systems.status.missing"),
      };
    }
    switch (systemStatus) {
      case "FINISHED":
        return {
          tone: "green",
          icon: CheckCircleIcon,
          label: t("customer.systems.status.linked"),
        };
      case "NOT_REQUIRED":
        return {
          tone: "gray",
          icon: MinusCircleIcon,
          label: t("customer.systems.status.not_required"),
        };
      default:
        return {
          tone: "amber",
          icon: ClockIcon,
          label: t("customer.systems.status.required"),
        };
    }
  })();

  const StatusIcon = statusPill.icon;
  const metaLine = [system?.serial_number, system?.placement]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <section
        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(20,28,46,0.05),0_1px_3px_rgba(20,28,46,0.05)]"
        aria-labelledby="customer-system-title"
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <h3
            id="customer-system-title"
            className="m-0 flex items-center gap-2.5 text-sm font-bold text-slate-900"
          >
            <span className="grid h-[26px] w-[26px] place-items-center rounded-lg bg-slate-100 text-slate-500">
              <BuildingOffice2Icon className="h-4 w-4" />
            </span>
            {t("customer.systems.card_title")}
          </h3>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_PILL[statusPill.tone]}`}
          >
            <StatusIcon className="h-3.5 w-3.5" />
            {statusPill.label}
          </span>
        </div>

        <div className="flex flex-col gap-2.5 px-5 py-4">
          {hasSystem ? (
            <>
              <div className="text-[15px] font-bold leading-snug text-slate-900">
                {system?.displayTitle ||
                  system?.system_m_name ||
                  system?.serial_number ||
                  t("System")}
              </div>
              {metaLine ? (
                <p className="m-0 text-[13px] text-slate-500">{metaLine}</p>
              ) : null}

              {addressLabel ? (
                <div className="flex flex-col gap-0.5 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    <MapPinIcon className="h-3.5 w-3.5" />
                    {t("customer.systems.address")}
                  </span>
                  <span className="text-[13px] font-medium text-slate-700">
                    {addressLabel}
                  </span>
                </div>
              ) : null}

              {comment ? (
                <div className="flex flex-col gap-0.5 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    {t("customer.systems.other_comment")}
                  </span>
                  <span className="whitespace-pre-wrap break-words text-[13px] font-medium text-slate-700">
                    {comment}
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <p className="m-0 text-sm text-slate-500">
              {t("customer.systems.not_linked")}
            </p>
          )}

          <div className="mt-1 flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg bg-[#1463C7] px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0B3A78]"
              onClick={() => setDialogOpen(true)}
            >
              {hasSystem ? t("Edit") : t("customer.systems.attach")}
            </button>

            {hasSystem && (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => setHistoryDialogOpen(true)}
              >
                {t("customer.systems.history")}
              </button>
            )}

            {hasSystem && (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
                disabled={!system?.product_id}
                onClick={() => {
                  if (system?.product_id) {
                    setProductSlideoverOpen(true);
                  } else {
                    toast.error(t("customer.systems.no_product_selected"));
                  }
                }}
              >
                {t("customer.systems.spare_parts")}
              </button>
            )}
          </div>
        </div>
      </section>

      <ProductSlideover
        open={productSlideoverOpen}
        onClose={() => setProductSlideoverOpen(false)}
        view="spareparts"
        productId={system?.product?.id || system?.product_id}
        module={module}
        moduleParam={moduleParam}
      />
      <ReportPreviousWorkDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        customerId={
          customer?.id ||
          system?.customer_id ||
          system?.customer?.id ||
          null
        }
        customerSystemId={system?.id}
        title="report.previous_work.facility_history_title"
        showFacilityBadge={false}
        emptyKey="report.previous_work.facility_history_empty"
      />
      <CustomerSystemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        readOnly={!!system?.id}
        selectedSystem={system?.id}
        customer={customer}
        onAttachSystem={handleOnAttachSystem}
        module={module}
        moduleParam={moduleParam}
        onRefresh={onRefresh}
        allowLinkActions={module === "report"}
      />
    </>
  );
};
